import { Checkbox } from '@mui/material';
import { getLabelFallback } from '@utils/utils';
import { useEffect, useState } from 'react';
import SelectBoxCL from './SelectBoxCL';
import { IContactLensDetails } from '@interfaces/myAppointmentInterfaces';

type Step1ContactProps = {
    isEditingPrescription: boolean;
    checkedDifferentCL?: boolean;
    editedRightCLDetails?: IContactLensDetails;
    editedLeftCLDetails?: IContactLensDetails;
    onContinue: (data: IStep1ContactData) => void;
    backToSelectedCLRight?: any;
    setBackToSelectedCLRight?: any;
    backToSelectedCLLeft?: any;
    setBackToSelectedCLLeft?: any;
    setIsDifferentClRightFromBack?: any;
    setIsDifferentClLeftFromBack?: any;
};

interface IStep1ContactData {
    checkedDifferentCL: boolean;
    selectedRightCL: any;
    selectedLeftCL: any;
}

const Step1Contact = (props: Step1ContactProps): JSX.Element => {
    const [checkedDifferentCL, setCheckedDifferentCL] = useState(false);
    const [selectedRightCL, setSelectedRightCL] = useState(null);
    const [selectedLeftCL, setSelectedLeftCL] = useState(null);
    const [clickedOnContinue, setClickedOnContinue] = useState(false);

    /**
     * Validate the step 1 form before calling onContinue
     */
    const checkBeforeClickContinue = () => {
        if (
            (checkedDifferentCL && selectedRightCL && selectedLeftCL) ||
            (!checkedDifferentCL && selectedRightCL)
        ) {
            if(!!props.backToSelectedCLRight && props.backToSelectedCLRight?.productId !== selectedRightCL?.productId){
                props.setIsDifferentClRightFromBack(true)
                if(!!selectedLeftCL === false){
                    props.setIsDifferentClLeftFromBack(true);
                }
            }else{
                props.setIsDifferentClRightFromBack(false)
                if(!!selectedLeftCL === false){
                    props.setIsDifferentClLeftFromBack(false);
                }
            }

            props.setBackToSelectedCLRight(selectedRightCL)
            
            if(selectedLeftCL && checkedDifferentCL){
                if(!!props.backToSelectedCLLeft && props.backToSelectedCLLeft?.productId !== selectedLeftCL?.productId){
                    props.setIsDifferentClLeftFromBack(true)
                }else{
                    if(!!props.backToSelectedCLLeft === false){
                        props.setIsDifferentClLeftFromBack(true)
                    }else{
                        props.setIsDifferentClLeftFromBack(false)
                    }
                }

                props.setBackToSelectedCLLeft(selectedLeftCL)
            }else{
                if(!!props.backToSelectedCLLeft && !!selectedLeftCL){
                    props.setIsDifferentClLeftFromBack(true)
                }
                props.setBackToSelectedCLLeft(null)
            }

            props.onContinue({
                checkedDifferentCL: checkedDifferentCL,
                selectedRightCL: selectedRightCL?.productId,
                selectedLeftCL: selectedLeftCL?.productId,
            });
        }
    };

    /**
     * If editing prescription, set as pre-selected the edited products in the select boxes
     */
    useEffect(() => {
        if (props.isEditingPrescription) {
            if (!!props?.checkedDifferentCL) {
                setCheckedDifferentCL(props?.checkedDifferentCL);
            }

            if (!!props?.editedRightCLDetails) {
                setSelectedRightCL({
                    productId: props?.editedRightCLDetails?.parentId,
                    productImageUrl: props?.editedRightCLDetails?.image,
                    productName: props?.editedRightCLDetails?.name,
                });
            }

            if (!!props?.editedLeftCLDetails) {
                setSelectedLeftCL({
                    productId: props?.editedLeftCLDetails?.parentId,
                    productImageUrl: props?.editedLeftCLDetails?.image,
                    productName: props?.editedLeftCLDetails?.name,
                });
            }
        }
    }, [props?.checkedDifferentCL, props?.editedRightCLDetails, props?.editedLeftCLDetails]);

    useEffect(() => {
        if(!!props.backToSelectedCLRight){
            setSelectedRightCL(props.backToSelectedCLRight)
        }

        if(!!props.backToSelectedCLLeft){
            setCheckedDifferentCL(true)
            setSelectedLeftCL(props.backToSelectedCLLeft)
        }

        try {
            window.tealium_data2track.push({
                id: 'Impression',
                Page_Section2: 'LensPrescription:CL1',
            });
        } catch (e) {
            console.error(e);
        }
    }, []);

    return (
        <div className="step1-container">
            <div className="step1-container-title">
                {getLabelFallback('MYPROFILE_MY_PRESCRIPTIONS_CONTACTS_STEP_1_TITLE')}
            </div>
            <div className="step1-container-checkbox">
                <Checkbox
                    size="small"
                    inputProps={{
                        'aria-label': 'Different Eyes',
                    }}
                    checked={checkedDifferentCL}
                    onChange={() => setCheckedDifferentCL(!checkedDifferentCL)}
                    data-analytics_available_call="0"
                />
                <div className="label">{getLabelFallback('ADD_PRESCRIPTION_STEP_1_CHECKBOX')}</div>
            </div>
            <div className="step1-container-selectbox">
                <div className="step1-container-selectbox-right">
                    {checkedDifferentCL && (
                        <div className="step1-container-selectbox-right-title">
                            {getLabelFallback('ADD_PRESCRIPTION_STEP_1_RIGHT')}
                        </div>
                    )}
                    <div className="step1-container-selectbox-right-inner">
                        <SelectBoxCL
                            isValid={!clickedOnContinue || (clickedOnContinue && selectedRightCL)}
                            selectedItem={selectedRightCL}
                            onSelectCL={setSelectedRightCL}
                        />
                    </div>
                </div>
                {checkedDifferentCL && (
                    <div className="step1-container-selectbox-left">
                        <div className="step1-container-selectbox-left-title">
                            {getLabelFallback('ADD_PRESCRIPTION_STEP_1_LEFT')}
                        </div>
                        <div className="step1-container-selectbox-left-inner">
                            <SelectBoxCL
                                isValid={
                                    !clickedOnContinue || (clickedOnContinue && selectedLeftCL)
                                }
                                selectedItem={selectedLeftCL}
                                onSelectCL={setSelectedLeftCL}
                            />
                        </div>
                    </div>
                )}
            </div>
            <div className="step1-container-button">
                <button
                    className="primary-button button-size-medium"
                    type="submit"
                    data-element-id="MyPrescription_Continue"
                    onClick={() => {
                        setClickedOnContinue(true);
                        checkBeforeClickContinue();
                    }}
                >
                    {getLabelFallback('ADD_PRESCRIPTION_STEP_1_BUTTON')}
                </button>
            </div>
        </div>
    );
};
export default Step1Contact;
